import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLocalData } from "../hooks/useLocalData";
import { useAuth } from "../hooks/useAuth";
import { calculateNoteBilling } from "../services/billingCalculation";
import {
  formatNoteForEmail,
  formatTranscriptForEmail,
  buildEmailSubject,
  openEmailWithNote,
  copyNoteAndTranscript,
} from "../services/emailService";
import { CheckCircle, Mail, Copy, FileText, ArrowRight, AlertTriangle } from "lucide-react";

export default function SubmittedPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { addNote, updateNote, recordCompletedSession } = useLocalData();
  const { user } = useAuth();
  const note = location.state?.note;
  const transcript = location.state?.transcript || "";
  const draftNoteId = location.state?.draftNoteId;
  const savedRef = useRef(false);
  const [copied, setCopied] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [billingInfo, setBillingInfo] = useState(null);

  // Save the note to local data store on first mount
  useEffect(() => {
    if (note && !savedRef.current) {
      savedRef.current = true;

      const duration = location.state?.duration || 1800;
      const durationMin = Math.round(duration / 60);

      // ─── ACCURATE BILLING CALCULATION ───
      // Uses 8-minute rule, service-specific rates, and proper HCPCS codes
      const billing = calculateNoteBilling({
        durationMinutes: durationMin,
        duration: `${durationMin} min`,
        serviceType: note.header?.serviceType || "ICM Weekly Visit",
        noteData: note,
      });

      setBillingInfo(billing);

      const noteEntry = {
        individualName: note.header?.individual || "Individual",
        individualId: location.state?.individualId || null,
        date: note.header?.date || new Date().toLocaleDateString(),
        serviceType: note.header?.serviceType || "ICM Weekly Visit",
        location: note.header?.site || note.header?.location || "Community",
        locationType: note.header?.location || "Out-of-Clinic",
        status: "submitted",
        // ─── Billing fields from accurate calculation ───
        billingCode: billing.code || "H0039",
        billingUnits: `${billing.units} unit${billing.units !== 1 ? "s" : ""}`,
        billingUnitsNum: billing.units,
        billingRate: billing.rate,
        billingReimbursement: billing.totalReimbursement,
        billingUnitType: billing.unitType,
        billingModifiers: billing.modifiers || [],
        billingWarnings: billing.warnings || [],
        billingCalculatedAt: billing.calculatedAt,
        durationMinutes: durationMin,
        duration: `${durationMin} min`,
        confidence: note.confidence || 90,
        hasPriorityFlags: (note.flaggedNotes || []).some((f) => f.priority === "priority"),
        noteData: note,
        transcript: transcript || null,
        flaggedNotes: null,
        extractedFacts: null,
        generationFailed: false,
      };

      // Also store billing info on the note object itself
      note.billingCode = billing.code;
      note.billingUnits = `${billing.units} unit${billing.units !== 1 ? "s" : ""}`;
      note.billingReimbursement = billing.totalReimbursement;
      note.billingLabel = billing.label;

      if (draftNoteId) {
        updateNote(draftNoteId, noteEntry);
      } else {
        addNote(noteEntry);
      }

      recordCompletedSession({
        individualName: note.header?.individual || "Individual",
        duration,
        aiGenerationTime: location.state?.aiGenerationTime || 25,
        confidence: note.confidence || 90,
      });
    }
  }, [note, draftNoteId, addNote, updateNote, recordCompletedSession, location.state, transcript]);

  const agentEmail = user?.email || "";

  const handleEmailNote = () => {
    openEmailWithNote({
      note,
      transcript,
      toEmail: agentEmail,
    });
    setEmailSent(true);
  };

  const handleCopyAll = async () => {
    const result = await copyNoteAndTranscript({ note, transcript });
    if (result.success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-surface-primary flex flex-col items-center justify-center px-8">
      <div
        className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
        style={{ background: "rgba(72,187,120,0.1)" }}
      >
        <CheckCircle size={40} className="text-green-400" />
      </div>

      <h2 className="text-2xl font-extrabold text-green-400 mb-2">Note Submitted</h2>
      <p className="text-sm text-gray-500 text-center leading-relaxed max-w-xs">
        Progress note for {note?.header?.individual || "individual"} has been finalized and
        is ready for EHR entry.
      </p>

      {/* Summary card */}
      <div
        className="w-full mt-6 rounded-xl p-4"
        style={{
          background: "rgba(15,23,42,0.5)",
          border: "1px solid rgba(99,179,237,0.06)",
        }}
      >
        {[
          ["Date", note?.header?.date || new Date().toLocaleDateString()],
          ["Duration", billingInfo ? `${billingInfo.durationMinutes} min` : (note?.header?.duration || "—")],
          ["Service", billingInfo?.label || note?.header?.serviceType || "—"],
          ["Billing Code", billingInfo?.code || note?.billingCode || "H0039"],
          ["Units", billingInfo ? `${billingInfo.units} unit${billingInfo.units !== 1 ? "s" : ""} (${billingInfo.unitType === "time" ? "15-min" : "encounter"})` : (note?.billingUnits || "—")],
          ["Est. Reimbursement", billingInfo ? `$${billingInfo.totalReimbursement.toFixed(2)}` : "—"],
          ["Transcript", transcript ? `${transcript.split(" ").length} words captured` : "Not available"],
          ["Audio", "Retained 72hrs then auto-deleted"],
        ].map(([label, value], i, arr) => (
          <div
            key={i}
            className="flex justify-between py-1.5"
            style={{ borderBottom: i < arr.length - 1 ? "1px solid rgba(99,179,237,0.06)" : "none" }}
          >
            <span className="text-xs text-gray-500">{label}</span>
            <span
              className="text-sm font-semibold"
              style={{
                color:
                  label === "Audio" ? "#ecc94b" :
                  label === "Est. Reimbursement" ? "#68d391" :
                  label === "Billing Code" || label === "Units" ? "#4fd1c5" :
                  label === "Transcript" ? "#63b3ed" :
                  "#cbd5e0",
              }}
            >
              {value}
            </span>
          </div>
        ))}
      </div>

      {/* Billing warnings */}
      {billingInfo?.warnings?.length > 0 && (
        <div
          className="w-full mt-3 rounded-xl p-3"
          style={{
            background: "rgba(236,201,75,0.04)",
            border: "1px solid rgba(236,201,75,0.12)",
          }}
        >
          <div className="flex items-center gap-1.5 mb-1">
            <AlertTriangle size={12} className="text-yellow-400" />
            <span className="text-[10px] font-bold text-yellow-400 uppercase tracking-wider">Billing Notice</span>
          </div>
          {billingInfo.warnings.map((w, i) => (
            <div key={i} className="text-xs text-gray-400 leading-relaxed">{w}</div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col gap-2.5 w-full mt-6">
        {/* Email — primary action */}
        <button
          className="w-full py-3.5 rounded-xl text-white font-semibold text-base flex items-center justify-center gap-2"
          style={{ background: "linear-gradient(135deg, #38a169, #2f855a)" }}
          onClick={handleEmailNote}
        >
          <Mail size={18} />
          {emailSent ? "Email Opened — Check Mail App" : "Email Note & Transcript"}
        </button>

        {/* Copy All */}
        <button
          className="w-full py-3.5 rounded-xl font-semibold text-base flex items-center justify-center gap-2"
          style={{
            background: "rgba(99,179,237,0.08)",
            color: "#63b3ed",
            border: "1px solid rgba(99,179,237,0.2)",
          }}
          onClick={handleCopyAll}
        >
          {copied ? (
            <>
              <CheckCircle size={16} /> Copied Note + Transcript
            </>
          ) : (
            <>
              <Copy size={16} /> Copy Note + Transcript
            </>
          )}
        </button>

        {/* View Note */}
        <button
          className="w-full py-3.5 rounded-xl font-semibold text-base flex items-center justify-center gap-2"
          style={{
            background: "rgba(79,209,197,0.06)",
            color: "#4fd1c5",
            border: "1px solid rgba(79,209,197,0.12)",
          }}
          onClick={() => navigate("/session/review", { state: { note, readOnly: true } })}
        >
          <FileText size={16} /> View Submitted Note
        </button>

        {/* Back to Dashboard */}
        <button
          className="w-full py-3.5 rounded-xl font-semibold text-base flex items-center justify-center gap-2"
          style={{
            background: "rgba(15,23,42,0.5)",
            color: "#718096",
            border: "1px solid rgba(99,179,237,0.06)",
          }}
          onClick={() => navigate("/dashboard")}
        >
          Return to Dashboard <ArrowRight size={14} />
        </button>
      </div>

      {/* Email hint */}
      {agentEmail && (
        <p className="text-[10px] text-gray-600 mt-4 text-center">
          Email will be sent to {agentEmail}
        </p>
      )}
    </div>
  );
}